import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
	Alert,
	Button,
	Card,
	Col,
	Descriptions,
	Modal,
	Row,
	Space,
	Table,
	Typography,
	message,
} from 'antd'
import {
	DemoAgentCredential,
	DemoDataStatus,
	DemoPackMeta,
	DemoSeedPackId,
	getDemoDataStatus,
	setupDemoData,
	teardownDemoData,
} from 'api/clients/demoData'

const { Text, Paragraph, Title } = Typography

interface DemoDataProps {
	clientId: string
}

const PACK_COPY: Record<
	DemoSeedPackId,
	{
		title: string
		description: string
		seedIntro: string
		seedBullets: string[]
		defaultHints: string[]
	}
> = {
	'website-screenshots': {
		title: 'Website screenshot pack',
		description:
			'Seeds CRM data matching dashcx.com/new Monitor, Recover, and Improve CX stories so you can capture real product screenshots.',
		seedIntro:
			'This creates Monitor / Recover / Improve CX demo data for product screenshots:',
		seedBullets: [
			'7 demo users (manager + 6 sales agents)',
			'Sales team, contacts, chats, calls',
			'Opportunities, wins, feedback, tickets',
			'Demo feedback template + completed campaign',
			'Demo chatbot',
		],
		defaultHints: [
			'Contact 360 — Sarah Mitchell',
			'Inbox / unattended list (Monitor attention feed)',
			'Omar Haddad chat + opportunity',
			'Opportunities board',
			'Feedback list — Sarah / Priya / Hannah',
			'Broadcasts — Demo weekly sales feedback',
			'Meera Iyer chat + ticket (Improve CX)',
			'Users / Demo Sales Team',
		],
	},
	'travel-agency': {
		title: 'Travel agency pack',
		description:
			'Horizon Travel staff ↔ client stories: enquiry, quote chase, visa docs, flight change, transfer ticket, feedback broadcast, and Work Report.',
		seedIntro: 'This creates travel-agency communication demo data:',
		seedBullets: [
			'Manager + sales / ops / visa / groups agents',
			'Hero threads (enquiry, quote chase, deposit, visa, ops, groups)',
			'Trip opportunities, tickets, reminders, feedback',
			'Post-trip feedback broadcast with replies',
			'AI summaries + today’s workspace digest',
		],
		defaultHints: [
			'Arjun Kapoor — long Italy family vacation thread + pending visa reply',
			'Arjun call cards — recording link + open transcript',
			'Ananya Sharma — Maldives enquiry chat',
			'Marcus Webb — overdue Europe quote + reminder',
			'Leila Hassan — deposit buying signal',
			'Sofia Rossi — missed visa call',
			'Mei-Lin Tan — CDG transfer ticket (Improve CX)',
			'Carl Nguyen — flight change / rebooking',
			'Noor Alami — visa document chase',
			'Broadcast — post-trip feedback + replies',
			'Work Report — travel daily digest',
		],
	},
	'hotel-industry': {
		title: 'Hotel industry pack',
		description:
			'Harborview sales WhatsApp (property/rooms, dates, recommendations, special requests) plus post-checkout praise and bad-experience recovery — not in-house ops chat.',
		seedIntro: 'This creates hotel sales + post-checkout demo data:',
		seedBullets: [
			'Sales / events / CX agents (not front-desk or housekeeping ops)',
			'Sales threads: property pick, check-in/out, packages, special requests, groups',
			'Post-checkout: praise, billing surprises, noise/cleanliness recovery',
			'Tags + chat labels + open AI Suggestions on heroes',
			'Post-stay feedback broadcast replies + Work Report digest',
		],
		defaultHints: [
			'Vikram Sethi — sales journey → post-checkout recovery (pending reply)',
			'Emma Walsh — room recommendation + special request',
			'James Okoro — Beach vs Downtown property selection',
			'Priya Desai — package upsell / deposit',
			'Linda Foster — post-checkout praise',
			'David / Marcus / Ken — post-checkout bad experience',
			'Tags & Labels + AI Suggestions',
			'Work Report — hotel sales + CX digest',
		],
	},
}

const PACK_ORDER: DemoSeedPackId[] = [
	'website-screenshots',
	'travel-agency',
	'hotel-industry',
]

const DemoData: React.FC<DemoDataProps> = ({ clientId }) => {
	const [status, setStatus] = useState<DemoDataStatus | null>(null)
	const [loading, setLoading] = useState(false)
	const [busyPack, setBusyPack] = useState<DemoSeedPackId | null>(null)

	const refresh = useCallback(async () => {
		if (!clientId) return
		try {
			setLoading(true)
			const data = await getDemoDataStatus(clientId)
			setStatus(data)
		} finally {
			setLoading(false)
		}
	}, [clientId])

	useEffect(() => {
		refresh()
	}, [refresh])

	const availableById = useMemo(() => {
		return new Map(
			(status?.available || []).map((meta: DemoPackMeta) => [meta.id, meta])
		)
	}, [status?.available])

	const packState = (packId: DemoSeedPackId) => {
		const fromPacks = status?.packs?.[packId]
		if (fromPacks) return fromPacks
		if (packId === 'website-screenshots') {
			return {
				seeded: !!status?.seeded,
				pack: packId,
				label: PACK_COPY[packId].title,
				run: status?.run ?? null,
			}
		}
		return {
			seeded: false,
			pack: packId,
			label: PACK_COPY[packId].title,
			run: null,
		}
	}

	const handleSetup = (packId: DemoSeedPackId) => {
		const copy = PACK_COPY[packId]
		Modal.confirm({
			title: `Seed ${copy.title}?`,
			width: 520,
			content: (
				<div>
					<p>{copy.seedIntro}</p>
					<ul>
						{copy.seedBullets.map(item => (
							<li key={item}>{item}</li>
						))}
					</ul>
					<p>
						Agent password: <Text code>Demo@DashCX1</Text>
					</p>
				</div>
			),
			okText: 'Seed demo data',
			onOk: async () => {
				try {
					setBusyPack(packId)
					const result = await setupDemoData(clientId, packId)
					message.success(result.message)
					await refresh()
				} finally {
					setBusyPack(null)
				}
			},
		})
	}

	const handleTeardown = (packId: DemoSeedPackId) => {
		const copy = PACK_COPY[packId]
		Modal.confirm({
			title: `Remove ${copy.title}?`,
			content:
				'Removes only tagged demo seed data for this pack (agents, contacts, chats, opportunities, template, campaign, etc.). Other packs and the primary account admin stay.',
			okText: 'Remove demo data',
			okButtonProps: { danger: true },
			onOk: async () => {
				try {
					setBusyPack(packId)
					await teardownDemoData(clientId, packId)
					message.success(`${copy.title} removed`)
					await refresh()
				} finally {
					setBusyPack(null)
				}
			},
		})
	}

	return (
		<Space direction='vertical' size='large' style={{ width: '100%' }}>
			<Alert
				type='info'
				showIcon
				message='Packs are independent — you can seed website, travel, hotel, or any combination on the same client.'
			/>
			<Row gutter={[16, 16]}>
				{PACK_ORDER.map(packId => {
					const copy = PACK_COPY[packId]
					const state = packState(packId)
					const meta = availableById.get(packId)
					const summary = state.run?.summary
					const credentials: DemoAgentCredential[] = state.run?.credentials || []
					const hints = meta?.screenshotHints?.length
						? meta.screenshotHints
						: copy.defaultHints
					const notes = meta?.notes || []
					const busy = busyPack === packId

					return (
						<Col xs={24} xl={8} key={packId}>
							<Card title={meta?.label || copy.title} loading={loading && !status}>
								<Paragraph type='secondary'>{copy.description}</Paragraph>
								<Alert
									type='info'
									showIcon
									style={{ marginBottom: 16 }}
									message='Template is APPROVED in-app for UI only. Real WhatsApp sends still need a Meta-approved template on the WABA.'
								/>
								<Space wrap>
									<Button
										type='primary'
										onClick={() => handleSetup(packId)}
										loading={busy}
										disabled={!!state.seeded || (!!busyPack && !busy)}
									>
										Seed demo data
									</Button>
									<Button
										danger
										onClick={() => handleTeardown(packId)}
										loading={busy}
										disabled={!state.seeded || (!!busyPack && !busy)}
									>
										Remove demo data
									</Button>
									<Button onClick={refresh} loading={loading} disabled={!!busyPack}>
										Refresh status
									</Button>
								</Space>
								<div style={{ marginTop: 16 }}>
									<Text>
										Status: <Text strong>{state.seeded ? 'Seeded' : 'Not seeded'}</Text>
									</Text>
								</div>
								{summary && (
									<Descriptions
										size='small'
										column={2}
										style={{ marginTop: 16 }}
										bordered
									>
										{Object.entries(summary).map(([key, value]) => (
											<Descriptions.Item key={key} label={key}>
												{value}
											</Descriptions.Item>
										))}
									</Descriptions>
								)}
								{credentials.length > 0 && (
									<div style={{ marginTop: 20 }}>
										<Title level={5}>Demo agent logins</Title>
										<Paragraph>
											Password for all demo agents:{' '}
											<Text code copyable>
												Demo@DashCX1
											</Text>
										</Paragraph>
										<Table
											size='small'
											rowKey='email'
											pagination={false}
											dataSource={credentials}
											columns={[
												{ title: 'Name', dataIndex: 'name' },
												{ title: 'Role', dataIndex: 'role' },
												{
													title: 'Email',
													dataIndex: 'email',
													render: (email: string) => <Text copyable>{email}</Text>,
												},
											]}
										/>
									</div>
								)}
								<Title level={5} style={{ marginTop: 24 }}>
									Suggested screenshot order
								</Title>
								<ol>
									{hints.map(hint => (
										<li key={hint}>{hint}</li>
									))}
								</ol>
								{notes.length > 0 && (
									<>
										<Title level={5}>Notes</Title>
										<ul>
											{notes.map(note => (
												<li key={note}>{note}</li>
											))}
										</ul>
									</>
								)}
							</Card>
						</Col>
					)
				})}
			</Row>
		</Space>
	)
}

export default DemoData
